import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Input,
  Form,
  message,
  Select,
  Spin,
  Popconfirm,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import AdminLayout from '../../../components/admin/AdminLayout';
import { adminApi, authApi } from '../../../modules/api';
import { clearSession } from '../../../modules/auth';
import './adminAdmins.css';

class AdminAdmins extends Component {
  formRef = React.createRef();

  editFormRef = React.createRef();

  state = {
    loading: true,
    admins: [],
    meta: {
      total: 0,
      page: 1,
      limit: 20,
      pages: 1,
    },
    search: '',
    creating: false,
    modalVisible: false,
    editingAdmin: null,
    updating: false,
    deletingId: null,
  };

  componentDidMount() {
    this.fetchAdmins();
  }

  async fetchAdmins(page = 1, search = this.state.search) {
    this.setState({ loading: true });
    try {
      const response = await adminApi.fetchAdmins({ page, search });
      const payload = response && response.data ? response.data : response;
      this.setState({
        loading: false,
        admins: payload.data || [],
        meta: payload.meta || this.state.meta,
        search,
      });
    } catch (error) {
      this.setState({ loading: false });
      message.error('Unable to load admin accounts.');
    }
  }

  openModal = () => {
    this.setState({ modalVisible: true }, () => {
      if (this.formRef.current) {
        this.formRef.current.resetFields();
      }
    });
  };

  closeModal = () => {
    this.setState({ modalVisible: false, creating: false });
  };

  openEditModal = (record) => {
    this.setState({ editingAdmin: record });
  };

  closeEditModal = () => {
    this.setState({ editingAdmin: null, updating: false });
  };

  handleUpdateAdmin = async (values) => {
    const { editingAdmin } = this.state;
    if (!editingAdmin) return;
    this.setState({ updating: true });
    try {
      await adminApi.updateAdmin(editingAdmin.id, {
        email: values.email.trim().toLowerCase(),
        name: (values.name || '').trim(),
        type: values.type,
      });
      message.success('Admin account updated.');
      this.closeEditModal();
      this.fetchAdmins(this.state.meta.page, this.state.search);
    } catch (error) {
      const errorMessage = error && error.response && error.response.data
        && error.response.data.message;
      message.error(errorMessage || 'Unable to update admin account.');
      this.setState({ updating: false });
    }
  };

  handleDeleteAdmin = async (record) => {
    this.setState({ deletingId: record.id });
    try {
      await adminApi.deleteAdmin(record.id);
      message.success('Admin account removed.');
      const { meta, search, admins } = this.state;
      const nextPage = meta.page > 1 && admins.length === 1 ? meta.page - 1 : meta.page;
      this.fetchAdmins(nextPage, search);
    } catch (error) {
      const errorMessage = error && error.response && error.response.data
        && error.response.data.message;
      message.error(errorMessage || 'Unable to remove admin account.');
    } finally {
      this.setState({ deletingId: null });
    }
  };

  handleSearchSubmit = () => {
    const { search } = this.state;
    this.fetchAdmins(1, search);
  };

  handleCreateAdmin = async (values) => {
    this.setState({ creating: true });
    try {
      await authApi.createAdmin({
        email: values.email.trim().toLowerCase(),
        name: values.name ? values.name.trim() : undefined,
        password: values.password,
        type: values.type || 'admin',
      });
      message.success('Admin account created.');
      this.closeModal();
      this.fetchAdmins();
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Failed to create admin account.';
      message.error(errMessage);
      this.setState({ creating: false });
    }
  };

  render() {
    const {
      loading,
      admins,
      meta,
      search,
      modalVisible,
      creating,
      editingAdmin,
      updating,
      deletingId,
    } = this.state;
    const { admin, onLogout } = this.props;
    const currentAdminId = admin && admin.user && admin.user.id;

    const columns = [
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (value) => value || '—',
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (value) => ({ superadmin: 'Superadmin', admin: 'Admin', viewer: 'Viewer' }[value] || value),
      },
      {
        title: 'Verified',
        dataIndex: 'isEmailVerified',
        key: 'verified',
        render: (value) => (value ? 'Yes' : 'No'),
      },
      {
        title: 'Last login',
        dataIndex: 'lastLoginAt',
        key: 'lastLogin',
        render: (value) => (value ? new Date(value).toLocaleString() : 'Never'),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value) => (value ? new Date(value).toLocaleDateString() : '—'),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (record.id === currentAdminId ? 'Current account' : (
          <div className="admin-admins__actions">
            <Button
              type="text"
              shape="circle"
              icon={<EditOutlined />}
              aria-label={`Edit ${record.email}`}
              title={`Edit ${record.email}`}
              onClick={() => this.openEditModal(record)}
            />
            <Popconfirm
              title={`Remove ${record.email}?`}
              description="Their admin access will end immediately. This cannot be undone."
              okText="Remove"
              okButtonProps={{ danger: true, loading: deletingId === record.id }}
              cancelText="Cancel"
              onConfirm={() => this.handleDeleteAdmin(record)}
            >
              <Button
                type="text"
                shape="circle"
                icon={<DeleteOutlined />}
                danger
                disabled={deletingId === record.id}
                aria-label={`Remove ${record.email}`}
                title={`Remove ${record.email}`}
              />
            </Popconfirm>
          </div>
        )),
      },
    ];

    return (
      <AdminLayout
        title="Admin Accounts"
        subtitle="Manage administrative access and invite new operators."
        activeKey="admins"
        admin={admin}
        onLogout={onLogout}
      >
        <div className="admin-admins__toolbar">
          <Input
            placeholder="Search by email or name"
            value={search}
            onChange={(event) => this.setState({ search: event.target.value })}
            onPressEnter={this.handleSearchSubmit}
            allowClear
          />
          <div className="admin-admins__actions">
            <Button onClick={this.handleSearchSubmit}>Search</Button>
            <Button type="primary" onClick={this.openModal}>
              New admin
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="admin-admins__loading">
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={admins}
            pagination={{
              current: meta.page,
              total: meta.total,
              pageSize: meta.limit,
              onChange: (pageNumber) => this.fetchAdmins(pageNumber),
            }}
          />
        )}

        <Modal
          open={modalVisible}
          title="Invite new admin"
          onCancel={this.closeModal}
          onOk={() => {
            if (this.formRef.current) {
              this.formRef.current.submit();
            }
          }}
          okText="Create admin"
          confirmLoading={creating}
          destroyOnHidden
        >
          <Form layout="vertical" ref={this.formRef} onFinish={this.handleCreateAdmin}>
            <Form.Item
              name="email"
              label="Admin email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input placeholder="admin@example.com" />
            </Form.Item>
            <Form.Item name="name" label="Name (optional)">
              <Input placeholder="Name shown in admin console" />
            </Form.Item>
            <Form.Item
              name="type"
              label="Admin type"
              initialValue="admin"
              rules={[{ required: true, message: 'Admin type is required' }]}
            >
              <Select>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="viewer">Viewer (read-only)</Select.Option>
                <Select.Option value="superadmin">Superadmin</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="password"
              label="Temporary password"
              rules={[
                { required: true, message: 'Password is required' },
                { min: 8, message: 'Use at least 8 characters' },
                {
                  pattern: /[a-z]/,
                  message: 'Include at least one lowercase letter',
                },
                {
                  pattern: /[0-9]/,
                  message: 'Include at least one number',
                },
              ]}
            >
              <Input.Password placeholder="Choose a secure password" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          open={Boolean(editingAdmin)}
          title={editingAdmin ? `Edit ${editingAdmin.email}` : 'Edit admin'}
          onCancel={this.closeEditModal}
          onOk={() => {
            if (this.editFormRef.current) this.editFormRef.current.submit();
          }}
          okText="Save changes"
          confirmLoading={updating}
          destroyOnHidden
        >
          <Form
            key={editingAdmin ? editingAdmin.id : 'none'}
            layout="vertical"
            ref={this.editFormRef}
            onFinish={this.handleUpdateAdmin}
            initialValues={editingAdmin ? {
              email: editingAdmin.email,
              name: editingAdmin.name || '',
              type: editingAdmin.type,
            } : {}}
          >
            <Form.Item
              name="email"
              label="Admin email"
              rules={[{ required: true, type: 'email', message: 'Enter a valid email address' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="name" label="Name">
              <Input />
            </Form.Item>
            <Form.Item name="type" label="Role" rules={[{ required: true, message: 'Choose a role' }]}>
              <Select>
                <Select.Option value="viewer">Viewer (read-only)</Select.Option>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="superadmin">Superadmin</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </AdminLayout>
    );
  }
}

AdminAdmins.propTypes = {
  admin: PropTypes.shape({
    user: PropTypes.object,
  }),
  onLogout: PropTypes.func.isRequired,
};

AdminAdmins.defaultProps = {
  admin: null,
};

const mapStateToProps = (state) => ({
  admin: state.auth.admin,
});

const mapDispatchToProps = {
  onLogout: clearSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminAdmins);
